import React from "react";
import {
  Dialog,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import {
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";

const TestResultDialog = ({ open, onClose, result }) => {
  if (!result) return null;

  const isSuccess = result.status === "sent" || result.status === "skipped";
  const isFailed = result.status === "failed";

  const templateName = result.testMode
    ? result.wouldSend?.template
    : result.templateUsed || result.wouldSend?.template || "—";

  const recipient = result.testMode
    ? result.wouldSend?.to
    : result.to || result.wouldSend?.to || "—";

  const parameters = result.testMode
    ? result.wouldSend?.parameters
    : result.parameters || result.wouldSend?.parameters;

  const paramDisplay =
    parameters && parameters.length > 0 ? parameters.join(", ") : "None";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxWidth: 400,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          pt: 2.5,
          pb: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
          Test Result
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: "#9ca3af" }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
        {/* Status */}
        <Box sx={{ mb: 2.5 }}>
          <Typography
            sx={{
              fontWeight: 700,
              textTransform: "uppercase",
              fontSize: "0.62rem",
              letterSpacing: 1,
              color: "#9ca3af",
              mb: 0.5,
            }}
          >
            Status
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            {isSuccess ? (
              <SuccessIcon sx={{ fontSize: 22, color: "#16a34a" }} />
            ) : (
              <ErrorIcon sx={{ fontSize: 22, color: "#dc2626" }} />
            )}
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "0.95rem",
                color: isSuccess ? "#16a34a" : "#dc2626",
              }}
            >
              {result.status}
            </Typography>
          </Box>
        </Box>

        {/* Divider */}
        <Box sx={{ borderBottom: "1px solid #f3f4f6", mb: 2.5 }} />

        {/* Error (only for failed) */}
        {isFailed && result.error && (
          <Box
            sx={{
              mb: 2.5,
              p: 2,
              bgcolor: "#fef2f2",
              borderRadius: 2,
              border: "1px solid #fecaca",
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                textTransform: "uppercase",
                fontSize: "0.62rem",
                letterSpacing: 1,
                color: "#dc2626",
                mb: 0.5,
              }}
            >
              Error
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: "#dc2626" }}>
              {result.error}
            </Typography>
          </Box>
        )}

        {/* Template */}
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              fontWeight: 700,
              textTransform: "uppercase",
              fontSize: "0.62rem",
              letterSpacing: 1,
              color: "#9ca3af",
              mb: 0.25,
            }}
          >
            Template
          </Typography>
          <Typography sx={{ fontSize: "0.9rem", fontWeight: 500 }}>
            {templateName}
          </Typography>
        </Box>

        {/* To */}
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              fontWeight: 700,
              textTransform: "uppercase",
              fontSize: "0.62rem",
              letterSpacing: 1,
              color: "#9ca3af",
              mb: 0.25,
            }}
          >
            To
          </Typography>
          <Typography sx={{ fontSize: "0.9rem", fontWeight: 500 }}>
            {recipient}
          </Typography>
        </Box>

        {/* Parameters */}
        <Box sx={{ mb: 1 }}>
          <Typography
            sx={{
              fontWeight: 700,
              textTransform: "uppercase",
              fontSize: "0.62rem",
              letterSpacing: 1,
              color: "#9ca3af",
              mb: 0.25,
            }}
          >
            Parameters
          </Typography>
          <Typography
            sx={{
              fontSize: "0.88rem",
              color: paramDisplay === "None" ? "#9ca3af" : "text.primary",
            }}
          >
            {paramDisplay}
          </Typography>
        </Box>

        {/* Divider */}
        <Box sx={{ borderBottom: "1px solid #f3f4f6", mt: 1 }} />
      </Box>

      {/* Footer */}
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            textTransform: "uppercase",
            fontWeight: 600,
            borderRadius: 2,
            px: 4,
            py: 0.8,
            fontSize: "0.82rem",
          }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TestResultDialog;
